// POPickerXL - Sprint 44 Fase 3 Vista 9.
// Autocomplete de Purchase Orders tokenizado.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import apiClient from '../../api/apiClient';

const CSS_ID = 'xl-po-picker-css';
const CSS = `
.xl-pop { position: relative; font-family: var(--xl-font-sans) }
.xl-pop__input {
  width: 100%;
  font: 400 14px/1.4 var(--xl-font-sans);
  color: var(--xl-color-text);
  background: var(--xl-color-bg);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 9px 12px;
  box-sizing: border-box;
}
.xl-pop__input:focus {
  outline: none;
  border-color: var(--xl-color-focus);
  box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
}
[data-xl-theme="dark"] .xl-pop__input:focus {
  box-shadow: 0 0 0 3px rgba(96,165,250,0.28);
}
.xl-pop__menu {
  position: absolute; z-index: 30;
  top: 100%; left: 0; right: 0;
  margin-top: 4px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  box-shadow: var(--xl-shadow-md);
  max-height: 300px; overflow-y: auto;
}
.xl-pop__row {
  display: block; width: 100%;
  text-align: left;
  background: transparent; border: none; cursor: pointer;
  padding: 10px 12px;
  font-family: inherit;
  color: var(--xl-color-text);
  border-bottom: 1px solid var(--xl-color-border);
}
.xl-pop__row:last-child { border-bottom: 0 }
.xl-pop__row:hover { background: var(--xl-color-surface-alt) }
.xl-pop__row-top { display: flex; align-items: center; justify-content: space-between }
.xl-pop__row-ref { font-family: var(--xl-font-mono); font-weight: 600; font-size: 12px }
.xl-pop__row-tot { font-family: var(--xl-font-mono); font-size: 11px; color: var(--xl-color-text-muted) }
.xl-pop__row-sub { font-size: 11px; color: var(--xl-color-text-muted); margin-top: 2px }
.xl-pop__hint { padding: 12px; font-size: 12px; color: var(--xl-color-text-muted) }
`;

export default function POPickerXL({ onSelectPO, placeholder = 'Buscar PO por referencia...' }) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const doSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await apiClient.get('/po/search?q=' + encodeURIComponent(q || '') + '&limit=10');
      setResults((r && r.data && r.data.items) || []);
    } catch (_) { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleInput = (v) => {
    setQuery(v); setOpen(true);
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => doSearch(v), 300);
  };

  const pick = (p) => {
    setQuery(p.referencia || '');
    setOpen(false);
    if (onSelectPO) onSelectPO(p);
  };

  return (
    <div ref={ref} className="xl-pop">
      <input type="text" className="xl-pop__input"
        value={query}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => { if (!results.length && !loading) doSearch(''); setOpen(true); }}
        placeholder={placeholder}
        autoComplete="off" />
      {open && (
        <div className="xl-pop__menu" role="listbox">
          {loading && <div className="xl-pop__hint">Buscando\u2026</div>}
          {!loading && results.length === 0 && <div className="xl-pop__hint">Sin resultados.</div>}
          {!loading && results.map((p) => (
            <button key={p.id} type="button" className="xl-pop__row" onClick={() => pick(p)}>
              <div className="xl-pop__row-top">
                <span className="xl-pop__row-ref">{p.referencia}</span>
                <span className="xl-pop__row-tot">
                  {p.moneda} {Number(p.total || 0).toLocaleString('es-CO')}
                </span>
              </div>
              <div className="xl-pop__row-sub">
                {p.proveedor_nombre || '\u2014'}{p.fecha ? ' \u00b7 ' + p.fecha : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
